<script setup lang="ts">
import type { BlogAd, BlogCategory, BlogKnowledge, BlogMenuItem, BlogNavigationCategory, BlogNewsArticle, BlogPost, BlogSidebarSection, BlogTag, NewsPage, PostPage, ThemeConfigDoc } from '~/composables/useBlogApi'
import qiniuLogo from '~/assets/brands/qiniu.png'
import aliyunLogo from '~/assets/brands/aliyun-icon.webp'
import { formatBlogDate } from '~/utils/date'
import { postViewCountEventName, storedPostViewCounts, type PostViewCountUpdate } from '~/composables/usePostViewCountSync'

const config = useRuntimeConfig()
const route = useRoute()
const { listPosts, listPostsByCategory, listPostsByTag, getMenu, getNavigation, getNews, getKnowledges, getThemeConfig, getCategories, getTags, getAds, getSidebar, searchPosts } = useBlogApi()
const { prefetchPost } = usePostPrefetch()
const siteUrl = String(config.public.siteUrl || 'https://example.com').replace(/\/$/, '')
const siteName = String(config.public.siteName || '云端笔记')
const title = `${siteName} - 记录思考，沉淀知识`
const description = '关注技术、工具与思考的个人博客。'
const feedPageSize = 4
const initialFeedPageSize = 6
const initialPage: PostPage = { list: [], total: 0, offset: 0, next_offset: 0, page_size: initialFeedPageSize, has_next: false }
const currentYear = new Date().getUTCFullYear()
const activeCategorySlug = computed(() => String(route.query.category || '').trim())
const activeTagSlug = computed(() => String(route.query.tag || '').trim())
const isKnowledgeRoute = computed(() => (route.path === '/knowledges' || route.path === '/') && activeCategorySlug.value.toLowerCase() === 'knowledge')
const isNewsRoute = computed(() => route.path === '/news')

const emptyTheme: ThemeConfigDoc = { theme_config: { title: '页面配置', configs: [], sub_configs: [] } }

async function loadInitialPostsWithRetry() {
  let lastError: unknown
  // A server process can be briefly unavailable during a rolling restart.
  // Retry before allowing Nitro's SWR layer to cache an SSR error response.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await listPosts(0, initialFeedPageSize)
    } catch (error) {
      lastError = error
      if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 180 * (attempt + 1)))
    }
  }
  throw lastError instanceof Error ? lastError : new Error('加载文章失败')
}

async function loadHomePosts() {
	if (isNewsRoute.value) return { list: [], total: 0, offset: 0, next_offset: 0, page_size: 0, has_next: false } as PostPage
  if (isKnowledgeRoute.value) return { list: [], total: 0, offset: 0, next_offset: 0, page_size: 0, has_next: false } as PostPage
  if (!activeCategorySlug.value && !activeTagSlug.value) return loadInitialPostsWithRetry()
  const list = activeCategorySlug.value
    ? await listPostsByCategory(activeCategorySlug.value)
    : await listPostsByTag(activeTagSlug.value)
  return { list, total: list.length, offset: 0, next_offset: list.length, page_size: list.length, has_next: false } as PostPage
}

const { data: initial, error, pending: initialPending } = await useAsyncData(() => `home-bootstrap:${isNewsRoute.value ? 'news' : activeCategorySlug.value || activeTagSlug.value || 'all'}`, async () => {
  const [posts, menu, navigation, news, knowledges, theme, categories, tags, ads, sidebar] = await Promise.all([
    loadHomePosts().catch(() => initialPage),
    getMenu().catch(() => [] as BlogMenuItem[]),
    getNavigation().catch(() => [] as BlogNavigationCategory[]),
    isNewsRoute.value ? getNews(0, 10).catch(() => ({ list: [], total: 0, offset: 0, next_offset: 0, page_size: 10, has_next: false } as NewsPage)) : Promise.resolve({ list: [], total: 0, offset: 0, next_offset: 0, page_size: 0, has_next: false } as NewsPage),
    getKnowledges().catch(() => [] as BlogKnowledge[]),
    getThemeConfig().catch(() => emptyTheme),
    getCategories().catch(() => [] as BlogCategory[]),
    getTags().catch(() => [] as BlogTag[]),
    getAds().catch(() => [] as BlogAd[]),
    getSidebar('home').catch(() => [] as BlogSidebarSection[])
  ])
  return { posts, menu, navigation, news, knowledges, theme, categories, tags, ads, sidebar }
}, {
  watch: [activeCategorySlug, activeTagSlug],
  default: () => ({ posts: initialPage, menu: [] as BlogMenuItem[], navigation: [] as BlogNavigationCategory[], news: { list: [], total: 0, offset: 0, next_offset: 0, page_size: 10, has_next: false } as NewsPage, knowledges: [] as BlogKnowledge[], theme: emptyTheme, categories: [] as BlogCategory[], tags: [] as BlogTag[], ads: [] as BlogAd[], sidebar: [] as BlogSidebarSection[] })
})

const posts = ref<BlogPost[]>(initial.value?.posts?.list ?? [])
const nextOffset = ref(initial.value?.posts?.next_offset ?? 0)
const hasNext = ref(initial.value?.posts?.has_next ?? false)
const menu = ref<BlogMenuItem[]>(initial.value?.menu ?? [])
const navigation = ref<BlogNavigationCategory[]>(initial.value?.navigation ?? [])
const news = ref<BlogNewsArticle[]>(initial.value?.news?.list ?? [])
const newsNextOffset = ref(initial.value?.news?.next_offset ?? 0)
const newsHasNext = ref(initial.value?.news?.has_next ?? false)
const newsLoadingMore = ref(false)
const newsLoadTrigger = ref<HTMLElement | null>(null)
const knowledges = ref<BlogKnowledge[]>(initial.value?.knowledges ?? [])
const navCollapsed = ref(false)
const activeNavId = ref<number | null>(null)
const navSearchKeyword = ref('')
const sortedNavigation = computed(() => [...navigation.value].sort((a, b) => (Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)) || (a.id - b.id)))
const visibleNavigation = computed(() => {
  const keyword = navSearchKeyword.value.trim().toLowerCase()
  if (!keyword) return sortedNavigation.value
  return sortedNavigation.value.map((group) => ({ ...group, navigations: (group.navigations || []).filter((item) => [item.title, item.name, item.description, item.url].some((value) => String(value || '').toLowerCase().includes(keyword))) })).filter((group) => group.navigations.length || [group.name, group.description].some((value) => String(value || '').toLowerCase().includes(keyword)))
})
const scrollToNavGroup = (id: number) => {
  activeNavId.value = id
  document.getElementById(`nav-group-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
const navIconName = (key?: string) => ({
  AnalyticsOutline: 'layout-dashboard', ArchiveOutline: 'archive', BookOutline: 'bookmark', BookmarksOutline: 'bookmark',
  BriefcaseOutline: 'bookmark', BuildOutline: 'settings', CameraOutline: 'sparkles', CafeOutline: 'sparkles',
  ChatbubblesOutline: 'message-circle', CloudOutline: 'folder-open', CodeSlashOutline: 'settings', ConstructOutline: 'settings',
  DesktopOutline: 'layout-dashboard', DocumentTextOutline: 'archive', DownloadOutline: 'arrow-up-right', EarthOutline: 'globe',
  ExtensionPuzzleOutline: 'sparkles', FilmOutline: 'sparkles', FitnessOutline: 'heart', FlameOutline: 'flame',
  FolderOpenOutline: 'folder-open', GameControllerOutline: 'sparkles', GlobeOutline: 'globe', HeartOutline: 'heart',
  HomeOutline: 'home', ImageOutline: 'sparkles', InformationCircleOutline: 'sparkles', LeafOutline: 'sparkles',
  LibraryOutline: 'bookmark', LinkOutline: 'link', MailOutline: 'message-circle', MapOutline: 'globe',
  MusicalNotesOutline: 'sparkles', NewspaperOutline: 'archive', NotificationsOutline: 'sparkles', PeopleOutline: 'user',
  PersonOutline: 'user', PhonePortraitOutline: 'layout-dashboard', PlanetOutline: 'globe', RestaurantOutline: 'sparkles',
  RocketOutline: 'arrow-up-right', SchoolOutline: 'bookmark', SearchOutline: 'search', SettingsOutline: 'settings',
  ShieldCheckmarkOutline: 'check', SparklesOutline: 'sparkles', StarOutline: 'sparkles', TimeOutline: 'archive',
  TodayOutline: 'archive', TrendingUpOutline: 'arrow-up-right', WarningOutline: 'sparkles', WifiOutline: 'link'
} as Record<string, string>)[String(key)] || 'folder-tree'
const theme = ref<ThemeConfigDoc>(initial.value?.theme ?? emptyTheme)
const publicCategories = ref<BlogCategory[]>(initial.value?.categories ?? [])
const publicTags = ref<BlogTag[]>(initial.value?.tags ?? [])
const publicAds = ref<BlogAd[]>(initial.value?.ads ?? [])
const publicSidebar = ref<BlogSidebarSection[]>(initial.value?.sidebar ?? [])
const postList = computed<BlogPost[]>(() => Array.isArray(posts.value) ? posts.value : [])
const feedSort = ref<'latest' | 'hot'>('latest')
const visiblePostList = computed(() => [...postList.value].sort((left, right) => {
  // Pinned posts are editorially ordered ahead of either user-selected sort.
  // Keep this comparison first so a high-viewed regular post never displaces one.
  const pinnedOrder = Number(Boolean(right.is_pinned)) - Number(Boolean(left.is_pinned))
  if (pinnedOrder) return pinnedOrder
  if (feedSort.value === 'hot') return Number(right.view_count || 0) - Number(left.view_count || 0) || Number(right.id) - Number(left.id)
  return new Date(right.created_at || right.published_at || 0).getTime() - new Date(left.created_at || left.published_at || 0).getTime() || Number(right.id) - Number(left.id)
}))
const loadingMore = ref(false)
const loadTrigger = ref<HTMLElement | null>(null)
const showNavDivider = ref(false)
const mobileMenuOpen = ref(false)
const searchOpen = ref(false)
const searchQuery = ref('')
const searchInput = ref<HTMLInputElement | null>(null)
const searchResults = ref<BlogPost[]>([])
const searchPending = ref(false)
const searchError = ref('')
const searchedKeyword = ref('')
let loadObserver: IntersectionObserver | undefined

function applyPostViewCount({ postID, viewCount }: PostViewCountUpdate) {
  if (!Number.isFinite(postID) || !Number.isFinite(viewCount)) return
  const update = (post: BlogPost) => Number(post.id) === postID && Number(post.view_count || 0) < viewCount
    ? { ...post, view_count: viewCount }
    : post
  posts.value = posts.value.map(update)
  publicSidebar.value = publicSidebar.value.map((section) => ({
    ...section,
    items: Array.isArray(section.items) ? section.items.map(update) : []
  }))
}

const handlePostViewCount = (event: Event) => {
  const update = (event as CustomEvent<PostViewCountUpdate>).detail
  if (update) applyPostViewCount(update)
}

const themeValues = computed(() => {
  const values: Record<string, string> = {}
  const walk = (node: any) => { for (const item of node?.configs || []) values[item.name] = item.value; for (const child of node?.sub_configs || []) walk(child); if (node?.sub_config) walk(node.sub_config) }
  walk(theme.value?.theme_config)
  return values
})
const seoTitle = computed(() => String(themeValues.value.seo_title || title).trim())
const seoDescription = computed(() => String(themeValues.value.seo_description || description).trim())
const seoKeywords = computed(() => String(themeValues.value.seo_keywords || '').trim())
const seoCanonical = computed(() => String(themeValues.value.seo_canonical || `${siteUrl}/`).trim())
const seoRobots = computed(() => String(themeValues.value.seo_robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1').trim())
const seoOgTitle = computed(() => String(themeValues.value.seo_og_title || seoTitle.value).trim())
const seoOgDescription = computed(() => String(themeValues.value.seo_og_description || seoDescription.value).trim())
const seoOgImage = computed(() => String(themeValues.value.seo_og_image || '').trim())
const siteLogo = computed(() => String(themeValues.value.site_logo || '').trim())
const faviconHref = siteLogo
const publicMenu = computed(() => {
  const items = menu.value.filter((item) => item.href !== '/nav' && item.name !== '导航').map((item) => {
    if (item.slug === 'knowledge' || item.name === '知识库' || item.name === '专题') return { ...item, href: '/knowledges', type: 'custom' }
    if (item.slug === 'news' || item.name === '新闻') return { ...item, href: '/news', type: 'custom' }
    return item
  })
  const navItem: BlogMenuItem = { type: 'custom', name: '导航', href: '/nav', sort_order: -1 }
  const archiveIndex = items.findIndex((item) => item.href === '/archives' || item.name === '归档')
  items.splice(archiveIndex >= 0 ? archiveIndex : items.length, 0, navItem)
  return items
})

const categories = computed(() => publicCategories.value.filter((item) => !item.parent_id))
const activeCategory = computed(() => categories.value.find((item) => item.slug === activeCategorySlug.value))
const activeTag = computed(() => publicTags.value.find((item) => item.slug === activeTagSlug.value))
const unlistedCategories = computed(() => {
  const menuHrefs = new Set(publicMenu.value.map((item) => item.href))
  return categories.value.filter((item) => !menuHrefs.has(`/category/${item.slug}`))
})
const homeSidebarAds = computed(() => publicAds.value.filter((item) => item.position === 'home_sidebar'))
function sidebarEnabled(name: string) { return String(themeValues.value[name] || '0') !== '0' }
function sidebarConfigList(name: string) {
  try { const value = JSON.parse(String(themeValues.value[name] || '[]')); return Array.isArray(value) ? value : [] } catch { return [] }
}
const sidebarAnnouncement = computed(() => sidebarEnabled('home_sidebar_announcement_show') && String(themeValues.value.home_sidebar_announcement_content || '').trim()
  ? String(themeValues.value.home_sidebar_announcement_content || '').trim() : '')
const sidebarSiteInfo = computed(() => sidebarEnabled('home_sidebar_site_show')
  ? { title: String(themeValues.value.home_sidebar_site_name || '本站信息'), description: String(themeValues.value.home_sidebar_site_description || ''), slogan: String(themeValues.value.home_sidebar_site_slogan || ''), entries: sidebarConfigList('home_sidebar_site_info_items') }
  : null)
const sidebarSections = computed<any[]>(() => {
  const custom = [
    sidebarEnabled('home_sidebar_media_show') && sidebarConfigList('home_sidebar_media_items').length
      ? { key: 'media', title: '媒体信息', sort_order: 1, entries: sidebarConfigList('home_sidebar_media_items') } : null
  ].filter(Boolean)
  return [...custom, ...publicSidebar.value].sort((left: any, right: any) => Number(left.sort_order || 0) - Number(right.sort_order || 0))
})
// Storage/CDN endpoints may enable hotlink protection. Do not leak the page
// referrer and keep image delivery identical in development and production.
const imageReferrerPolicy = 'no-referrer'
const footerLinks = computed(() => [1, 2, 3]
  .map((index) => ({
    text: themeValues.value[`footer_link_${index}_text`] || '',
    url: themeValues.value[`footer_link_${index}_url`] || ''
  }))
  .filter((item) => item.text && item.url))
const newsImage = (article: BlogNewsArticle) => String(article.image_url || (article as any).pic_url || (article as any).picUrl || '').trim()
const hideBrokenImage = (event: Event) => { const image = event.currentTarget as HTMLImageElement | null; if (image) image.style.display = 'none' }

const postHref = (post: BlogPost) => `/post/${post.slug}`
const categoryHref = (slug: string) => slug === 'nav' ? '/nav' : slug === 'knowledge' ? '/knowledges' : slug === 'news' ? '/news' : ({ path: '/', query: { category: slug } })
const tagHref = (slug: string) => ({ path: '/', query: { tag: slug } })
const menuHref = (item: BlogMenuItem) => item.type === 'category' && item.slug ? categoryHref(item.slug) : item.href
const articleLevelTag = (level: number | undefined) => {
  switch (Number(level)) {
    case 0:
      return { label: '注册会员', className: 'border-sky-300/25 bg-gradient-to-r from-sky-300/16 via-cyan-200/10 to-blue-400/16 text-sky-200 shadow-[0_0_14px_rgba(56,189,248,0.12)]' }
    case 1:
      return { label: '黄金VIP', className: 'border-amber-300/25 bg-gradient-to-r from-amber-300/18 via-yellow-200/12 to-amber-500/18 text-amber-200 shadow-[0_0_14px_rgba(251,191,36,0.12)]' }
    case 2:
      return { label: '钻石VIP', className: 'border-violet-300/25 bg-gradient-to-r from-violet-300/18 via-fuchsia-200/12 to-cyan-300/18 text-violet-200 shadow-[0_0_14px_rgba(167,139,250,0.14)]' }
    default:
      return null
  }
}

function openSearch() {
  searchOpen.value = true
  nextTick(() => searchInput.value?.focus())
}

function closeSearch() {
  searchOpen.value = false
  searchError.value = ''
}

function escapeHTML(value: string) {
  return value.replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character] || character)
}

function highlightSearch(value: string) {
  const source = String(value || '')
  const keyword = searchedKeyword.value
  if (!keyword) return escapeHTML(source)
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const matcher = new RegExp(escapedKeyword, 'gi')
  let html = ''
  let cursor = 0
  for (const match of source.matchAll(matcher)) {
    const index = match.index ?? 0
    html += escapeHTML(source.slice(cursor, index))
    html += `<mark class="search-highlight">${escapeHTML(match[0])}</mark>`
    cursor = index + match[0].length
  }
  return html + escapeHTML(source.slice(cursor))
}

async function submitSearch() {
  const keyword = searchQuery.value.trim()
  if (!keyword) {
    searchInput.value?.focus()
    return
  }
  searchPending.value = true
  searchError.value = ''
  searchedKeyword.value = keyword
  try {
    searchResults.value = await searchPosts(keyword)
  } catch {
    searchResults.value = []
    searchError.value = '搜索暂时不可用，请稍后重试。'
  } finally {
    searchPending.value = false
  }
}

async function loadMore() {
  if (loadingMore.value || !hasNext.value) return
  loadingMore.value = true
  try {
    const page = await listPosts(nextOffset.value, feedPageSize)
    const existing = new Set(posts.value.map((post) => post.id))
    const added = page.list.filter((post) => !existing.has(post.id))
    posts.value.push(...added)
    nextOffset.value = page.next_offset
    hasNext.value = page.has_next
  } finally {
    loadingMore.value = false
  }
}

async function loadMoreNews() {
  if (!isNewsRoute.value || newsLoadingMore.value || !newsHasNext.value) return
  newsLoadingMore.value = true
  try {
    const page = await getNews(newsNextOffset.value, 5)
    const existing = new Set(news.value.map((item) => item.id))
    news.value.push(...page.list.filter((item) => !existing.has(item.id)))
    newsNextOffset.value = page.next_offset
    newsHasNext.value = page.has_next
  } finally { newsLoadingMore.value = false }
}

const updateNavDivider = () => {
	showNavDivider.value = window.scrollY > 32
}

onMounted(() => {
	storedPostViewCounts().forEach(applyPostViewCount)
	window.addEventListener(postViewCountEventName, handlePostViewCount as EventListener)
	updateNavDivider()
	window.addEventListener('scroll', updateNavDivider, { passive: true })
	if (loadTrigger.value && 'IntersectionObserver' in window) {
		loadObserver = new IntersectionObserver(([entry]) => {
			if (entry?.isIntersecting) void loadMore()
		}, { rootMargin: '320px 0px' })
		loadObserver.observe(loadTrigger.value)
	}
	if (newsLoadTrigger.value && 'IntersectionObserver' in window) {
		loadObserver = new IntersectionObserver(([entry]) => { if (entry?.isIntersecting) void loadMoreNews() }, { rootMargin: '320px 0px' })
		loadObserver.observe(newsLoadTrigger.value)
	}
})

onBeforeUnmount(() => {
	loadObserver?.disconnect()
	window.removeEventListener(postViewCountEventName, handlePostViewCount as EventListener)
	window.removeEventListener('scroll', updateNavDivider)
})

watch(initial, (value) => {
  posts.value = value?.posts?.list ?? []
  nextOffset.value = value?.posts?.next_offset ?? 0
  hasNext.value = value?.posts?.has_next ?? false
  news.value = value?.news?.list ?? []
  newsNextOffset.value = value?.news?.next_offset ?? 0
  newsHasNext.value = value?.news?.has_next ?? false
  menu.value = value?.menu ?? []
  theme.value = value?.theme ?? emptyTheme
  publicCategories.value = value?.categories ?? []
  publicTags.value = value?.tags ?? []
  publicAds.value = value?.ads ?? []
  publicSidebar.value = value?.sidebar ?? []
})

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoOgTitle.value,
  ogDescription: () => seoOgDescription.value,
  ogImage: () => seoOgImage.value || undefined,
  ogType: 'website',
  ogUrl: () => seoCanonical.value,
  twitterCard: 'summary_large_image',
  robots: () => seoRobots.value
})

useHead(() => ({
  meta: seoKeywords.value ? [{ name: 'keywords' as const, content: seoKeywords.value }] : [],
  link: [
    { rel: 'canonical' as const, href: seoCanonical.value },
    ...(faviconHref.value ? [{ rel: 'icon' as const, href: faviconHref.value }] : [])
  ],
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: seoTitle.value,
      url: seoCanonical.value,
      description: seoDescription.value,
      inLanguage: 'zh-CN'
    })
  }]
}))
</script>

<template>
  <div class="home-shell flex min-h-screen flex-col text-[#e2e8f0]">
    <PublicSiteHeader v-if="route.path !== '/nav'" :menu="menu" :categories="categories" :logo="siteLogo" :site-title="themeValues.site_title || siteName" :site-subtitle="themeValues.site_subtitle || '记录技术沉淀与实践'" :show-divider="showNavDivider" @open-search="openSearch" />

    <main class="flex-1">
      <div class="mx-auto w-full max-w-[1280px] px-4 py-8 sm:px-6 sm:py-10 lg:px-8 xl:px-0">
        <div v-if="route.path !== '/nav' && route.path !== '/knowledges' && !isKnowledgeRoute && !isNewsRoute" class="grid gap-10 min-[900px]:grid-cols-[minmax(0,1fr)_220px] min-[900px]:gap-10 xl:grid-cols-[minmax(0,1fr)_260px] xl:gap-14">
          <section aria-labelledby="latest-posts">
            <div v-if="activeCategory || activeTag" class="category-filter-bar">
              <span><AppIcon :name="activeCategory ? 'folder-open' : 'tags'" class="size-4" /> 正在浏览：{{ activeCategory?.name || activeTag?.name || activeTagSlug }}</span>
              <NuxtLink to="/" class="category-filter-clear">查看全部</NuxtLink>
            </div>
            <div v-if="error && postList.length === 0" class="home-empty py-12 text-center text-sm text-[#94a3b8]">
              暂时无法加载文章，请稍后重试。
            </div>

            <div v-else-if="postList.length" class="home-feed">
              <div v-if="!activeCategory && !activeTag" class="feed-sort-tabs" role="tablist" aria-label="文章排序">
                <button type="button" :class="{ active: feedSort === 'latest' }" role="tab" :aria-selected="feedSort === 'latest'" @click="feedSort = 'latest'">最新</button>
                <button type="button" :class="{ active: feedSort === 'hot' }" role="tab" :aria-selected="feedSort === 'hot'" @click="feedSort = 'hot'">热门</button>
              </div>
              <article
                v-for="(post, index) in visiblePostList"
                :key="post.id"
                v-memo="[post.id, post.title, post.excerpt, post.view_count, post.like_count, post.comment_count, post.article_level, post.post_bg_signed_url]"
                class="home-entry group"
                :class="post.post_bg_signed_url ? 'grid gap-5 sm:grid-cols-[1fr_172px] sm:items-stretch sm:gap-8' : 'block'"
                @pointerenter="prefetchPost(post.slug)"
                @focusin="prefetchPost(post.slug)"
              >
                <div class="min-w-0">
                  <div class="mb-3 flex items-center gap-2 text-xs text-[#94a3b8]">
                  <NuxtLink v-if="post.category" :to="categoryHref(post.category.slug)" class="post-category"><AppIcon name="folder-tree" class="mr-1 size-3" />{{ post.category.name }}</NuxtLink>
                    <time :datetime="post.published_at || post.created_at">{{ formatBlogDate(post.published_at || post.created_at) }}</time>
                    <span v-if="post.is_pinned" class="ml-1 rounded-full bg-[#fb7185]/14 px-2 py-0.5 text-[10px] font-bold text-[#fda4af]">置顶</span>
                  </div>
                  <h3 class="text-xl font-bold leading-snug tracking-[-0.035em] text-[#e2e8f0] sm:text-[22px]">
                    <NuxtLink :to="postHref(post)" class="article-title-link line-clamp-2" :title="post.title">{{ post.title }}</NuxtLink>
                  </h3>
                  <span
                    v-if="articleLevelTag(post.article_level)"
                    class="absolute right-3 top-0 z-10 inline-flex -translate-y-1/2 items-center gap-1 rounded-full border bg-[#101b2f] px-2 py-0.5 text-[10px] font-extrabold tracking-[0.05em]"
                    :class="articleLevelTag(post.article_level)?.className"
                  >
                    <AppIcon :name="post.article_level === 2 ? 'diamond' : 'crown'" class="size-3.5" />
                    <span>{{ articleLevelTag(post.article_level)?.label }}</span>
                  </span>
                  <p v-if="post.excerpt" class="mt-3 line-clamp-2 text-sm leading-7 text-[#94a3b8]">{{ post.excerpt }}</p>
                  <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#94a3b8]">
                    <span class="inline-flex items-center gap-1"><AppIcon name="eye" class="size-3.5" />{{ post.view_count }}</span>
                    <span class="inline-flex items-center gap-1"><AppIcon name="heart" class="size-3.5" />{{ post.like_count }}</span>
                    <span class="inline-flex items-center gap-1"><AppIcon name="message-circle" class="size-3.5" />{{ post.comment_count }}</span>
                    <span v-if="post.tags?.length" class="inline-flex flex-wrap items-center gap-1.5 border-l border-[#31415c] pl-3"><AppIcon name="tags" class="size-3.5 text-[#a78bfa]" /><NuxtLink v-for="tag in post.tags" :key="tag.id || tag.slug || tag.name" :to="tagHref(tag.slug)" class="post-tag">{{ tag.name }}</NuxtLink></span>
                  </div>
                </div>

                <NuxtLink v-if="post.post_bg_signed_url" :to="postHref(post)" :aria-label="`阅读文章：${post.title}`" class="order-first block aspect-[16/10] overflow-hidden bg-[#1f2a40] sm:order-last sm:aspect-auto sm:h-full">
                  <img :src="post.post_bg_signed_url" alt="" width="344" height="215" :loading="index < 2 ? 'eager' : 'lazy'" :fetchpriority="index === 0 ? 'high' : 'auto'" :referrerpolicy="imageReferrerPolicy" decoding="async" class="size-full object-fill transition-transform duration-500 ease-out group-hover:scale-[1.035]" />
                </NuxtLink>
              </article>
            </div>

            <div v-else class="home-empty py-12 text-center text-sm text-[#94a3b8]">暂无已发布文章。</div>

            <div ref="loadTrigger" class="flex h-20 items-center justify-center">
              <span v-if="loadingMore" class="feed-loading text-sm">正在载入文章</span>
              <span v-else-if="postList.length && !hasNext" class="text-sm text-[#64748b]">已经到底了</span>
            </div>
          </section>

          <aside class="home-sidebar space-y-5 min-[900px]:pt-1">
            <section v-if="sidebarAnnouncement" class="home-sidebar-section sidebar-announcement">
              <h2 class="sidebar-heading"><AppIcon name="megaphone" class="size-4 sidebar-announcement-icon" /><span class="sidebar-heading-text">公告</span></h2>
              <p class="mt-4 whitespace-pre-line text-sm leading-7 text-[#94a3b8]">{{ sidebarAnnouncement }}</p>
            </section>
            <section v-if="sidebarSiteInfo" class="home-sidebar-section">
              <h2 class="sidebar-heading"><AppIcon name="folder-tree" class="size-4" /><span class="sidebar-heading-text">{{ sidebarSiteInfo.title }}</span></h2>
              <div class="mt-4">
                <p v-if="sidebarSiteInfo.description" class="text-sm leading-7 text-[#94a3b8]">{{ sidebarSiteInfo.description }}</p>
                <p v-if="sidebarSiteInfo.slogan" class="sidebar-site-slogan">{{ sidebarSiteInfo.slogan }}</p>
                <div v-if="sidebarSiteInfo.entries?.length" class="mt-4 grid gap-2">
                  <div v-for="(entry, index) in sidebarSiteInfo.entries" :key="`${entry.name}-${index}`" class="flex items-center gap-3 text-sm text-[#94a3b8]"><img v-if="entry.image" :src="entry.image" :alt="entry.name || '信息图片'" class="sidebar-site-info-image" loading="lazy" :referrerpolicy="imageReferrerPolicy" /><span>{{ entry.name }}</span></div>
                </div>
              </div>
            </section>
            <section v-if="categories.length" class="home-sidebar-section">
              <h2 class="sidebar-heading"><AppIcon name="folder-open" class="size-4" /><span class="sidebar-heading-text">分类</span></h2>
              <ul class="mt-4 space-y-2.5">
                <li v-for="category in categories" :key="category.id">
                  <NuxtLink :to="categoryHref(category.slug)" class="sidebar-link">{{ category.name }}<AppIcon name="arrow-up-right" class="size-3.5" /></NuxtLink>
                </li>
              </ul>
            </section>

            <section v-if="homeSidebarAds.length" class="home-sidebar-section">
              <h2 class="sidebar-heading"><AppIcon name="megaphone" class="size-4" /><span class="sidebar-heading-text">推荐内容</span></h2>
              <div class="mt-4 space-y-3">
                <a v-for="ad in homeSidebarAds" :key="ad.id" :href="ad.link_url || undefined" :target="ad.link_url ? '_blank' : undefined" :aria-label="ad.title || '打开推荐内容'" rel="noopener noreferrer" class="home-ad block overflow-hidden">
                  <img v-if="ad.image_signed_url" :src="ad.image_signed_url" :alt="ad.title" loading="lazy" :referrerpolicy="imageReferrerPolicy" decoding="async" class="aspect-[16/7] w-full object-cover" />
                  <span v-else class="block px-3 py-2 text-sm text-[#c7d2e4]">{{ ad.title }}</span>
                </a>
              </div>
            </section>

            <section v-for="section in sidebarSections" :key="section.key" class="home-sidebar-section">
              <h2 class="sidebar-heading">
                <AppIcon :name="section.key === 'media' ? 'sparkles' : section.key === 'site-info' ? 'folder-tree' : section.key === 'stats' ? 'folder-tree' : section.key === 'hot' ? 'flame' : section.key === 'random' ? 'dices' : section.key === 'tagcloud' ? 'tags' : section.key === 'links' ? 'link' : 'sparkles'" class="size-4" />
                <span class="sidebar-heading-text">{{ section.title }}</span>
              </h2>
              <div v-if="section.key === 'media'" class="mt-4 space-y-3">
                <article v-for="(entry, index) in section.entries" :key="`${entry.title}-${index}`" class="sidebar-media-entry">
                  <a v-if="entry.image" :href="entry.image" target="_blank" rel="noopener noreferrer" class="sidebar-media-image-link" title="点击查看原图">
                    <img :src="entry.image" :alt="entry.title || '媒体图片'" class="sidebar-media-image" loading="lazy" :referrerpolicy="imageReferrerPolicy" />
                  </a>
                  <div class="min-w-0"><p class="text-sm font-medium text-[var(--text)]">{{ entry.title }}</p><p v-if="entry.subtitle" class="mt-1 text-xs leading-5 text-[#94a3b8]">{{ entry.subtitle }}</p></div>
                </article>
              </div>
              <div v-else-if="section.key === 'stats'" class="sidebar-stats" aria-label="站点数据">
                <div class="sidebar-stat"><strong>{{ section.stats?.posts ?? 0 }}</strong><span>文章</span></div>
                <div class="sidebar-stat"><strong>{{ section.stats?.categories ?? 0 }}</strong><span>分类</span></div>
                <div class="sidebar-stat"><strong>{{ section.stats?.tags ?? 0 }}</strong><span>标签</span></div>
              </div>
              <div v-else-if="section.key === 'tagcloud'" class="sidebar-tag-cloud mt-4">
                <NuxtLink v-for="tag in section.tags || []" :key="tag.id" :to="tagHref(tag.slug)" class="sidebar-tag-link" :class="{ active: activeTagSlug === tag.slug }" :aria-current="activeTagSlug === tag.slug ? 'page' : undefined">{{ tag.name }}</NuxtLink>
                <p v-if="!(section.tags || []).length" class="sidebar-empty">暂无标签</p>
              </div>
              <div v-else-if="section.key === 'links'" class="mt-4 flex flex-wrap gap-x-3 gap-y-2">
                <a v-for="link in section.links || []" :key="link.id" :href="link.url" target="_blank" rel="noopener noreferrer" class="text-xs text-[#94a3b8] transition-colors hover:text-[#67e8f9]">{{ link.name }}</a>
                <p v-if="!(section.links || []).length" class="sidebar-empty">暂无友情链接</p>
              </div>
              <ul v-else class="sidebar-post-list mt-5">
                <li v-for="(post, index) in section.items" :key="post.id">
                  <NuxtLink :to="postHref(post)" class="sidebar-post-link" @pointerenter="prefetchPost(post.slug)" @focus="prefetchPost(post.slug)">
                    <span class="sidebar-post-rank">{{ Number(index) + 1 }}</span>
                    <img v-if="section.show_bg && post.post_bg_signed_url" :src="post.post_bg_signed_url" alt="" loading="lazy" decoding="async" :referrerpolicy="imageReferrerPolicy" class="sidebar-post-image" />
                    <span class="min-w-0 flex-1">
                      <span class="sidebar-post-title">{{ post.title }}</span>
                      <span class="sidebar-post-meta">
                        <span><AppIcon name="eye" class="size-3" />{{ post.view_count }}</span>
                        <span><AppIcon name="bookmark" class="size-3" />{{ post.like_count }}</span>
                        <span><AppIcon name="message-circle" class="size-3" />{{ post.comment_count }}</span>
                      </span>
                    </span>
                  </NuxtLink>
                </li>
              </ul>
            </section>

            <section class="home-sidebar-section">
              <h2 class="sidebar-heading"><AppIcon name="sparkles" class="size-4" /><span class="sidebar-heading-text">关于本站</span></h2>
              <p class="mt-4 text-sm leading-7 text-[#94a3b8]">一个记录技术、工具与思考的独立博客。内容持续更新中。</p>
            </section>
          </aside>
        </div>
        <section v-else-if="isNewsRoute" class="news-page mx-auto w-full max-w-[920px]">
          <div class="mb-8"><p class="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Latest News</p><h1 class="mt-2 text-3xl font-bold tracking-tight text-[#e2e8f0]">新闻资讯</h1><p class="mt-2 text-sm text-[#94a3b8]">AI 与技术领域的最新动态</p></div>
          <div v-if="initialPending && !news.length" class="grid gap-4"><div v-for="i in 5" :key="i" class="animate-pulse rounded-2xl border border-white/10 bg-white/[0.03] p-5"><div class="h-5 w-3/4 rounded bg-white/10" /><div class="mt-4 h-3 w-full rounded bg-white/10" /><div class="mt-2 h-3 w-2/3 rounded bg-white/10" /></div></div>
          <div v-else-if="news.length" class="grid gap-4">
            <a v-for="article in news" :key="article.id" :href="article.url" target="_blank" rel="noopener noreferrer" class="news-entry group flex gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition duration-300 hover:-translate-y-0.5 hover:border-cyan-300/30 hover:bg-white/[0.06]"><img v-if="newsImage(article)" :src="newsImage(article)" :alt="article.title" loading="lazy" referrerpolicy="no-referrer" class="h-24 w-32 shrink-0 rounded-xl object-cover" @error="hideBrokenImage" /><div class="min-w-0 flex-1"><h2 class="text-lg font-semibold leading-7 text-[#e2e8f0] transition group-hover:text-cyan-200">{{ article.title }}</h2><p v-if="article.description" class="mt-2 line-clamp-2 text-sm leading-6 text-[#94a3b8]">{{ article.description }}</p><div class="mt-3 flex flex-wrap gap-3 text-xs text-[#64748b]"><span>{{ article.source_name || '未知来源' }}</span><time v-if="article.published_at">{{ formatBlogDate(article.published_at) }}</time></div></div></a>
          </div>
          <p v-else class="home-empty py-16 text-center text-sm text-[#94a3b8]">暂无新闻</p>
          <div ref="newsLoadTrigger" class="flex h-20 items-center justify-center"><span v-if="newsLoadingMore" class="feed-loading text-sm">正在载入新闻</span><span v-else-if="news.length && !newsHasNext" class="text-sm text-[#64748b]">已经到底了</span></div>
        </section>
        <section v-else-if="route.path === '/nav'" class="nav-content" :class="{ 'nav-sidebar-collapsed': navCollapsed }">
          <aside class="nav-category-panel" :class="{ collapsed: navCollapsed }"><button class="nav-collapse-btn" type="button" :aria-label="navCollapsed ? '展开分类导航' : '收缩分类导航'" :title="navCollapsed ? '展开分类导航' : '收缩分类导航'" @click="navCollapsed = !navCollapsed"><AppIcon :name="navCollapsed ? 'chevron-right' : 'chevron-left'" /></button><button v-for="group in sortedNavigation" :key="group.id" type="button" class="nav-category-item" :class="{ active: activeNavId === group.id }" @click="scrollToNavGroup(group.id)"><AppIcon :name="navIconName(group.icon_key)" class="nav-category-icon" /><span v-if="!navCollapsed">{{ group.name }}</span><b v-if="!navCollapsed">{{ group.navigations?.length || 0 }}</b></button></aside>
          <div class="nav-resource-area"><div class="nav-nav-top"><button type="button" class="nav-home-link" @click="navigateTo('/')">返回主页</button><label class="nav-search"><AppIcon name="search" /><input v-model="navSearchKeyword" type="search" placeholder="搜索导航" aria-label="搜索导航" /></label></div><div v-if="visibleNavigation.length" class="nav-groups"><section v-for="group in visibleNavigation" :id="`nav-group-${group.id}`" :key="group.id" class="nav-group"><div class="nav-group-head"><h2><AppIcon :name="navIconName(group.icon_key)" class="nav-group-icon" />{{ group.name }}</h2><span>{{ group.description }}</span></div><div class="nav-cards"><a v-for="item in group.navigations" :key="item.id" :href="item.url" target="_blank" rel="noopener noreferrer" class="nav-card"><img v-if="item.logo_url" :src="item.logo_url" :alt="item.title" loading="lazy"><span v-else class="nav-fallback">{{ item.title.slice(0, 1) }}</span><span class="nav-card-copy"><strong>{{ item.title }}</strong><small>{{ item.description || item.name }}</small></span></a></div></section></div><p v-else class="nav-empty">暂无匹配的导航资源</p></div>
        </section>
        <section v-else class="knowledge-public-page">
          <div class="knowledge-public-heading"><h1>知识库</h1><p>整理值得长期学习与实践的专题内容</p></div>
          <div v-if="knowledges.length" class="knowledge-public-grid">
            <NuxtLink v-for="item in knowledges" :key="item.id" :to="`/knowledge/${item.id}`" class="knowledge-public-card">
              <div class="knowledge-public-cover"><img v-if="item.cover_url" :src="item.cover_url" :alt="item.title" loading="lazy" /><span v-else><AppIcon name="book" class="size-10" /></span></div>
              <div class="knowledge-public-body"><h2>{{ item.title }}</h2><div class="knowledge-public-meta"><span class="knowledge-public-views"><AppIcon name="eye" class="size-4" />{{ item.view_count ?? 0 }}</span><time v-if="item.created_at">{{ formatBlogDate(item.created_at) }}</time></div></div>
            </NuxtLink>
          </div>
          <p v-else class="nav-empty">暂无公开知识库</p>
        </section>
      </div>
    </main>

    <PublicSiteFooter :values="themeValues" :site-name="siteName" />

    <Teleport to="body">
      <div v-if="searchOpen" class="search-overlay" role="dialog" aria-modal="true" aria-label="搜索文章" @click.self="closeSearch">
        <form class="search-dialog" @submit.prevent="submitSearch" @keydown.esc="closeSearch">
          <div class="search-dialog-heading">
            <label for="site-search">搜索文章</label>
            <button type="button" class="search-close" aria-label="关闭搜索" @click="closeSearch">Esc</button>
          </div>
          <div class="search-input-wrap">
            <AppIcon name="search" class="size-5 shrink-0 text-[#67e8f9]" />
            <input id="site-search" ref="searchInput" v-model="searchQuery" type="search" maxlength="100" autocomplete="off" placeholder="输入关键词，按 Enter 搜索" class="min-w-0 flex-1 bg-transparent text-base text-[#e2e8f0] outline-none placeholder:text-[#64748b]" />
            <button type="submit" class="cursor-pointer text-sm font-semibold text-[#67e8f9] transition-colors hover:text-[#cffafe]">搜索</button>
          </div>
          <div v-if="searchedKeyword" class="search-results" aria-live="polite">
            <p v-if="searchPending" class="search-result-status">正在搜索...</p>
            <p v-else-if="searchError" class="search-result-status search-result-error">{{ searchError }}</p>
            <p v-else-if="!searchResults.length" class="search-result-status">没有找到与“{{ searchedKeyword }}”相关的文章。</p>
            <div v-else class="search-result-list">
              <NuxtLink v-for="post in searchResults" :key="post.id" :to="postHref(post)" class="search-result-item" @pointerenter="prefetchPost(post.slug)" @focus="prefetchPost(post.slug)" @click="closeSearch">
                <span class="search-result-meta"><span v-if="post.category">{{ post.category.name }}</span><time :datetime="post.published_at || post.created_at">{{ formatBlogDate(post.published_at || post.created_at) }}</time></span>
                <span class="search-result-title" v-html="highlightSearch(post.title)" />
                <span v-if="post.excerpt" class="search-result-excerpt" v-html="highlightSearch(post.excerpt)" />
              </NuxtLink>
            </div>
          </div>
        </form>
      </div>
    </Teleport>
  </div>
</template>
